import { CardType, DefaultRequiredFieldsForCard, IFeatures, PermissionsPhase1, RequiredFieldsForCard } from '@bciti/types';
import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Subject, of } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';

import { FeaturesService } from 'src/app/services/features/features.service';
import { ToastrService } from 'ngx-toastr';

@Component({
    selector: 'app-features-form',
    templateUrl: './features-form.component.html',
    styleUrls: ['./features-form.component.scss'],
})
export class FeaturesFormComponent implements OnInit, OnChanges, OnDestroy {
    private destroy$ = new Subject();

    @Input() orgTag: string;
    orgFeaturesFromDB: IFeatures;
    CardType = CardType;

    loading = false;
    editing = false;
    creating = false;

    form = new FormGroup({
        card: new FormGroup({
            BCITI: new FormGroup({
                canUpdate: new FormControl({ value: false, disabled: true }, [Validators.required]),
                discrepancies: new FormControl({ value: false, disabled: true }, [Validators.required]),
            }),
            CITIZEN: new FormGroup({
                canUpdate: new FormControl({ value: false, disabled: true }, [Validators.required]),
                discrepancies: new FormControl({ value: false, disabled: true }, [Validators.required]),
            }),
            SPORTS: new FormGroup({
                canUpdate: new FormControl({ value: false, disabled: true }, [Validators.required]),
                discrepancies: new FormControl({ value: false, disabled: true }, [Validators.required]),
            }),
            LIBRARY: new FormGroup({
                canUpdate: new FormControl({ value: false, disabled: true }, [Validators.required]),
                discrepancies: new FormControl({ value: false, disabled: true }, [Validators.required]),
            }),
            LEGACY: new FormGroup({
                canUpdate: new FormControl({ value: false, disabled: true }, [Validators.required]),
                discrepancies: new FormControl({ value: false, disabled: true }, [Validators.required]),
            }),
        }),
        cardRequiredFields: new FormGroup({}),
        news: new FormGroup({
            sync: new FormControl({ value: false, disabled: true }, [Validators.required]),
        }),
        notices: new FormGroup({
            sync: new FormControl({ value: false, disabled: true }, [Validators.required]),
        }),
        requests: new FormGroup({
            sync: new FormControl({ value: false, disabled: true }, [Validators.required]),
            status: new FormGroup({
                enableAdminChanges: new FormControl({ value: false, disabled: true }, [Validators.required]),
            }),
            pgRequetesIframe: new FormGroup({
                active: new FormControl({ value: false, disabled: true }, [Validators.required]),
                url: new FormControl({ value: null, disabled: true }),
            }),
        }),
        pgpermit: new FormGroup({
            url: new FormControl({ value: null, disabled: true }),
        }),
        requestCategories: new FormGroup({
            sync: new FormControl({ value: false, disabled: true }, [Validators.required]),
        }),
        serviceState: new FormGroup({
            sync: new FormControl({ value: false, disabled: true }, [Validators.required]),
        }),
        family: new FormGroup({
            link: new FormGroup({
                enableExpiration: new FormControl({ value: false, disabled: true }, [Validators.required]),
            }),
        }),
        address: new FormGroup({
            avs: new FormControl({ value: false, disabled: true }, [Validators.required]),
        }),
        preventAdminNotifications: new FormGroup({}),
    });

    get cardRequiredFieldsForm() {
        return this.form.get('cardRequiredFields') as FormGroup;
    }

    get preventAdminNotificationsForm() {
        return this.form.get('preventAdminNotifications') as FormGroup;
    }

    get pgRequetesIframeForm() {
        return this.form.get('requests.pgRequetesIframe') as FormGroup;
    }

    get pgPermitForm() {
        return this.form.get('pgpermit') as FormGroup;
    }

    constructor(private featuresService: FeaturesService, private toastrService: ToastrService) {}

    ngOnInit(): void {
        this.pgRequetesIframeForm.controls.active.valueChanges.subscribe((active) => {
            let urlControl = this.pgRequetesIframeForm.controls.url;
            active ? urlControl.setValidators(Validators.required) : urlControl.clearValidators();
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.orgTag.currentValue) {
            this.loading = true;
            this.form.disable();
            this.featuresService
                .getFeatures(this.orgTag)
                .pipe(
                    take(1),
                    map((features) => {
                        this.form.patchValue(features);

                        this.orgFeaturesFromDB = features;
                        this.editing = true;

                        Object.values(RequiredFieldsForCard).forEach((requiredFieldForCard) => {
                            const form = new FormControl(features.cardRequiredFields?.includes(requiredFieldForCard));
                            this.cardRequiredFieldsForm.addControl(requiredFieldForCard, form);
                        });

                        this.availableModules.forEach((module) => {
                            const form = new FormGroup({
                                prevent: new FormControl(features.preventAdminNotifications?.[module]?.prevent),
                                distributionList: new FormControl(this.formatDistributionList(features.preventAdminNotifications?.[module]?.distributionList)),
                            });

                            this.preventAdminNotificationsForm.addControl(module, form);
                        });

                        this.loading = false;

                        this.form.enable();
                    }),
                    catchError((error) => {
                        if (error.status === 404) {
                            // Org doesn't have a features config.
                            this.loading = false;
                            this.editing = false;
                            this.creating = true;
                            this.form.enable();
                            return of();
                        }
                        console.error(error);
                        this.toastrService.error(error.message, error.error);
                        this.loading = false;
                        this.editing = false;
                        this.form.disable();
                        return of();
                    }),
                )
                .subscribe();
        }
    }

    handleSubmit(): void {
        if (this.form.untouched && !this.creating) {
            this.toastrService.warning('No changes');
            return;
        }
        if (this.creating) {
            this.createConfig();
        } else {
            this.updateConfig();
        }
    }

    isModulePreventEnabled(module: PermissionsPhase1): boolean {
        return this.preventAdminNotificationsForm.get(module)?.get('prevent').value;
    }

    createConfig(): void {
        if (this.form.valid) {
            this.loading = true;
            this.form.disable();
            const data = this.buildPayload();

            this.featuresService
                .createFeatures(this.orgTag, data)
                .pipe(
                    take(1),
                    map((response) => {
                        this.orgFeaturesFromDB = response;
                        this.toastrService.success('Features config created');
                        this.loading = false;
                        this.editing = true;
                        this.creating = false;
                        this.form.enable();
                    }),
                    catchError((error) => {
                        console.error(error);
                        this.toastrService.error(error.error, error.message);
                        this.loading = false;
                        this.form.enable();
                        return of();
                    }),
                )
                .subscribe();
        }
    }

    updateConfig(): void {
        if (this.form.valid) {
            this.loading = true;
            this.form.disable();
            const data = this.buildPayload();

            this.featuresService
                .updateFeatures(this.orgTag, data)
                .pipe(
                    take(1),
                    map((response) => {
                        this.orgFeaturesFromDB = response;
                        this.toastrService.success('Features saved');
                        this.loading = false;
                        this.editing = true;
                        this.form.enable();
                    }),
                    catchError((error) => {
                        console.error(error);
                        this.toastrService.error(error.error, error.message);
                        this.loading = false;
                        this.form.enable();
                        return of();
                    }),
                )
                .subscribe();
        }
    }

    reset(): void {
        this.form.reset(this.orgFeaturesFromDB);
        this.toastrService.info('Form reset');
    }

    startCreating(): void {
        this.editing = true;
        this.creating = true;
    }

    buildPayload(): IFeatures {
        const data = { ...this.form.value } as IFeatures;
        data.cardRequiredFields = this.handleCardRequiredFields() as RequiredFieldsForCard[];
        data.preventAdminNotifications = this.handlePreventAdminNotifications();
        return data;
    }

    handlePreventAdminNotifications(): IFeatures['preventAdminNotifications'] {
        const preventAdminNotifications = this.preventAdminNotificationsForm.value ?? {};
        return this.availableModules.reduce((acc, module) => {
            const moduleValue = preventAdminNotifications[module];
            if (moduleValue) {
                acc[module] = {
                    ...moduleValue,
                    distributionList: this.parseDistributionList(moduleValue.distributionList),
                };
            }
            return acc;
        }, {} as IFeatures['preventAdminNotifications']);
    }

    /** The stored value is a string[], but older configs hold a comma separated string. */
    formatDistributionList(distributionList: string[] | string): string {
        return Array.isArray(distributionList) ? distributionList.join(', ') : distributionList ?? '';
    }

    /** The input holds a comma separated string, the API expects a string[]. */
    parseDistributionList(distributionList: string[] | string): string[] {
        if (Array.isArray(distributionList)) {
            return distributionList;
        }
        return (distributionList ?? '')
            .split(',')
            .map((email) => email.trim())
            .filter((email) => !!email);
    }

    handleCardRequiredFields(): string[] {
        const cardRequiredFieldsArray = this.transformCardRequiredFields(this.form.value.cardRequiredFields);
        this.defaultRequiredFieldsForCard.forEach((value) => {
            if (!cardRequiredFieldsArray.includes(RequiredFieldsForCard[value.toUpperCase()])) {
                cardRequiredFieldsArray.push(RequiredFieldsForCard[value.toUpperCase()]);
            }
        });
        return cardRequiredFieldsArray;
    }

    transformCardRequiredFields(cardRequiredFields) {
        return Object.keys(cardRequiredFields).filter((key) => cardRequiredFields[key]);
    }

    canDeleteCardControl(cardType: CardType): boolean {
        switch (cardType) {
            case CardType.BCITI:
                return !!this.form.get('card.CITIZEN');

            case CardType.CITIZEN:
                return !!this.form.get('card.BCITI');

            default:
                console.error('unhandled cardType in canDeleteCardControl', cardType);
                return true;
        }
    }

    get requiredFieldsForCard(): string[] {
        return Object.values(RequiredFieldsForCard) as string[];
    }
    get defaultRequiredFieldsForCard(): string[] {
        return Object.values(DefaultRequiredFieldsForCard) as string[];
    }

    get availableModules() {
        return Object.values(PermissionsPhase1);
    }

    removeCardControl(cardControlName: string) {
        switch (cardControlName) {
            case 'BCITI':
                (this.form.get('card') as FormGroup).removeControl('BCITI');
                break;

            case 'CITIZEN':
                (this.form.get('card') as FormGroup).removeControl('CITIZEN');
                break;

            default:
                console.error('unhandled cardControlName for removeCardControl(): ', cardControlName);
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
